/**
 * 20260928 - Canvas overlay feature module: the four inbound verbs of the `?page=canvas-overlay`
 * route (Unity's CanvasWebOverlayPopupPanel, which shows the web hamburger / Scene info / Shot info /
 * Preferences over the live canvas) plus its two outbound verbs.
 *
 *   showView           {view, source, sceneId, shotId, thumbnailUrl?, hasCustomMedia?}
 *   setOverlayContext  {source, rows: [{id, enabled}]}          the menu's four context rows
 *   setOverlayRegion   {left, top, width, height} | null        the fold-split touch region
 *   setWhatsNewBadge   {hasNew}                                 every host that draws the row
 *   setOrientTargets   {targets: [{id, left, top, width, height, onScreen}]}
 *                                                               20261002, the Shot Editor coach
 *                                                               marks' native controls (window
 *                                                               fractions, top-left origin)
 *   setOverlayTopBar   {bottom: number | null}                  20261006, the opener's native top
 *                                                               bar's bottom edge (a fraction of the
 *                                                               web view's height from its top);
 *                                                               null = no bar shown / not measured
 *
 * …and, outbound for the coach marks (CanvasOrient.tsx): `canvasOrientStep {id}` every time a step
 * becomes the current one, `canvasOrientFinished {outcome}` exactly once per walk, and the dev-only
 * `resetCanvasOrientSeen {}` (Unity owns the seen flag; web storage is wiped app-wide).
 *
 * LEAF STORES ONLY: the overlay's own store (components/CanvasOverlay/canvasOverlayStore.ts) and
 * accountStore. Never uiStore — that edge is the uiStore ↔ embeddedBridge cycle features/settings.ts
 * already documents, and nothing here needs it.
 *
 * `setWhatsNewBadge` is a table verb for the routes that run initBridge (the board and this
 * overlay); the Projects home has no table, so its receiver chain hands the same message to the
 * same handler through `parseSetWhatsNewBadge` below — the payload guard exists once.
 */

import { flushSync } from 'react-dom';
import type { HandlerMap } from '../dispatch';
import { diagLog, enqueueSendToNative, sendDirectNavigation } from '../transport';
import { getLocaleLoadInFlight } from './settings';
import { useAccountStore } from '@/store/accountStore.ts';
import {
  useCanvasOverlayStore,
  CANVAS_ORIENT_TARGET_IDS,
  CANVAS_OVERLAY_ROW_IDS,
  CANVAS_OVERLAY_VIEWS,
  type CanvasOrientTarget,
  type CanvasOrientTargetId,
  type CanvasOverlayRegion,
  type CanvasOverlayRow,
  type CanvasOverlayRowId,
  type CanvasOverlayShowView,
  type CanvasOverlaySource,
  type CanvasOverlayView,
} from '@/components/CanvasOverlay/canvasOverlayStore';

// ─── Message types ───────────────────────────────────────────────────────

export interface ShowViewMessage {
  type: 'showView';
  payload: {
    view: CanvasOverlayView;
    source: CanvasOverlaySource;
    sceneId: number;
    shotId: number;
    thumbnailUrl?: string;
    hasCustomMedia?: boolean;
  };
}

export interface SetOverlayContextMessage {
  type: 'setOverlayContext';
  payload: { source: CanvasOverlaySource; rows: Array<{ id: CanvasOverlayRowId; enabled: boolean }> };
}

export interface SetOverlayRegionMessage {
  type: 'setOverlayRegion';
  payload: CanvasOverlayRegion | null;
}

export interface SetWhatsNewBadgeMessage {
  type: 'setWhatsNewBadge';
  payload: { hasNew: boolean };
}

export interface SetOrientTargetsMessage {
  type: 'setOrientTargets';
  payload: {
    targets: Array<{
      id: CanvasOrientTargetId;
      left: number;
      top: number;
      width: number;
      height: number;
      onScreen: boolean;
    }>;
  };
}

export interface SetOverlayTopBarMessage {
  type: 'setOverlayTopBar';
  payload: { bottom: number | null };
}

export type CanvasOverlayMessage =
  | ShowViewMessage
  | SetOverlayContextMessage
  | SetOverlayRegionMessage
  | SetWhatsNewBadgeMessage
  | SetOrientTargetsMessage
  | SetOverlayTopBarMessage;

/** How a coach-mark walk ended (`canvasOrientFinished`): Got it on the last step, Escape / the back
 *  ladder, or the page gave up before showing anything (no targets within 2 s, or too few). */
export type CanvasOrientOutcome = 'completed' | 'skipped' | 'aborted';

// ─── Payload guards ──────────────────────────────────────────────────────

function readSource(raw: unknown): CanvasOverlaySource {
  return raw === 'viewfinder' ? 'viewfinder' : 'canvas';
}

function readId(raw: unknown): number {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
}

/** showView's guard. The view is the one field that must be right — an unknown view has nothing to
 *  open — so a bad one drops the message; every other field falls back to its "not said" value. */
export function readShowViewPayload(raw: unknown): CanvasOverlayShowView | null {
  const p = raw as Partial<ShowViewMessage['payload']> | null | undefined;
  if (p === null || typeof p !== 'object') return null;
  if (!(CANVAS_OVERLAY_VIEWS as readonly unknown[]).includes(p.view)) return null;
  return {
    view: p.view as CanvasOverlayView,
    source: readSource(p.source),
    sceneId: readId(p.sceneId),
    shotId: readId(p.shotId),
    thumbnailUrl: typeof p.thumbnailUrl === 'string' && p.thumbnailUrl.length > 0 ? p.thumbnailUrl : null,
    hasCustomMedia: typeof p.hasCustomMedia === 'boolean' ? p.hasCustomMedia : null,
  };
}

/** setOverlayContext's rows: known ids with a boolean `enabled`, anything else dropped. */
export function readOverlayRows(raw: unknown): CanvasOverlayRow[] {
  if (!Array.isArray(raw)) return [];
  const out: CanvasOverlayRow[] = [];
  for (const item of raw) {
    const r = item as { id?: unknown; enabled?: unknown } | null;
    if (r === null || typeof r !== 'object') continue;
    if (!(CANVAS_OVERLAY_ROW_IDS as readonly unknown[]).includes(r.id)) continue;
    if (typeof r.enabled !== 'boolean') continue;
    out.push({ id: r.id as CanvasOverlayRowId, enabled: r.enabled });
  }
  return out;
}

/**
 * setOverlayRegion's guard. Window fractions with a top-left origin: every field finite, the origin
 * in 0..1, the size in (0..1], and the rectangle inside the window. Anything else — including the
 * explicit `null` Unity sends when the window is not split (which dispatchMessage hands over as the
 * whole message, `payload ?? raw`) — reads as "no region", the answer every unsplit host gives.
 */
export function readOverlayRegion(raw: unknown): CanvasOverlayRegion | null {
  const r = raw as Partial<CanvasOverlayRegion> | null | undefined;
  if (r === null || r === undefined || typeof r !== 'object') return null;
  const fields = [r.left, r.top, r.width, r.height];
  if (fields.some((n) => typeof n !== 'number' || !Number.isFinite(n))) return null;
  const { left, top, width, height } = r as CanvasOverlayRegion;
  const EPS = 1e-6;
  if (left < 0 || top < 0 || width <= 0 || height <= 0) return null;
  if (left + width > 1 + EPS || top + height > 1 + EPS) return null;
  return { left, top, width, height };
}

/**
 * setOrientTargets' guard (20261002). Null when the payload carries no `targets` array at all (the
 * message is then dropped and the store keeps what it had). Otherwise every entry is checked on
 * its own and a bad one is dropped without taking the others with it: a known id, four finite
 * numbers, a positive size (a zero-size rect is nothing to point at) and a boolean `onScreen`.
 * The origin is deliberately NOT range-checked — Unity reports an off-screen control's true
 * rectangle. A repeated id keeps its first entry: the page hangs one `data-orient` element per id,
 * and CoachMark aims at the first match in document order.
 */
export function readOrientTargets(raw: unknown): CanvasOrientTarget[] | null {
  const p = raw as { targets?: unknown } | null | undefined;
  if (p === null || p === undefined || typeof p !== 'object' || !Array.isArray(p.targets)) return null;
  const out: CanvasOrientTarget[] = [];
  const seen = new Set<string>();
  for (const item of p.targets) {
    const t = item as Partial<Record<keyof CanvasOrientTarget, unknown>> | null;
    if (t === null || typeof t !== 'object') continue;
    if (!(CANVAS_ORIENT_TARGET_IDS as readonly unknown[]).includes(t.id)) continue;
    const id = t.id as CanvasOrientTargetId;
    if (seen.has(id)) continue;
    const nums = [t.left, t.top, t.width, t.height];
    if (nums.some((n) => typeof n !== 'number' || !Number.isFinite(n))) continue;
    const [left, top, width, height] = nums as number[];
    if (width <= 0 || height <= 0) continue;
    if (typeof t.onScreen !== 'boolean') continue;
    seen.add(id);
    out.push({ id, left, top, width, height, onScreen: t.onScreen });
  }
  return out;
}

/**
 * setOverlayTopBar's guard (20261006). The bar's bottom edge as a fraction of the web view's
 * height: finite and strictly inside (0, 1), anything else — a missing payload, an explicit
 * `bottom: null` (no bar shown), a bar flush with an edge — reads as null, and the macOS sheet falls
 * back to its fixed title-bar strip (overlayRegion.ts).
 */
export function readOverlayTopBar(raw: unknown): number | null {
  const p = raw as { bottom?: unknown } | null | undefined;
  if (p === null || p === undefined || typeof p !== 'object') return null;
  const b = p.bottom;
  if (typeof b !== 'number' || !Number.isFinite(b)) return null;
  return b > 0 && b < 1 ? b : null;
}

/** Projects-route receiver chain: narrow an inbound message to setWhatsNewBadge (the shape check
 *  only — the handler below owns the payload guard, exactly like projectsBridge.parseSetFoldState). */
export function parseSetWhatsNewBadge(msg: unknown): SetWhatsNewBadgeMessage | null {
  if (typeof msg !== 'object' || msg === null) return null;
  if ((msg as { type?: unknown }).type !== 'setWhatsNewBadge') return null;
  return msg as SetWhatsNewBadgeMessage;
}

// ─── Inbound handlers ────────────────────────────────────────────────────

/**
 * Store the requested view, MOUNT it, then acknowledge — in that order, and the order is the
 * contract with the host: Unity keeps a warm overlay hidden until `overlayViewShown` (bounded at
 * 500 ms), so the ack has to mean "the page has switched", not "the message arrived".
 *
 *  1. Wait for a locale load still in flight (features/settings.ts), so the first revealed frame is
 *     already in the user's language rather than English for one round trip.
 *  2. flushSync around the store write. The page reacts to `showSeq` from a synchronous store
 *     subscription and opens the view with ordinary React state updates; flushSync commits all of
 *     them before it returns. Without it the commit would be scheduled, and a hidden WKWebView
 *     paints nothing that would make it land sooner.
 *  3. The ack goes by its own guarded navigation (transport.sendDirectNavigation), not the outbox:
 *     the host is waiting on it, and the outbox is only drained when something pings it.
 */
function showView(msg: ShowViewMessage): void {
  const show = readShowViewPayload(msg.payload);
  if (show === null) {
    diagLog('CanvasOverlay', `showView dropped: malformed payload ${JSON.stringify(msg.payload)}`);
    return;
  }
  const commit = () => {
    flushSync(() => { useCanvasOverlayStore.getState().applyShowView(show); });
    sendDirectNavigation('overlayViewShown', { view: show.view });
  };
  const localeLoad = getLocaleLoadInFlight();
  if (localeLoad === null) { commit(); return; }
  // Both arms: a failed language load must still show the view (in English) rather than never ack.
  localeLoad.then(commit, commit);
}

export const canvasOverlayHandlers = {
  showView,
  setOverlayContext: (msg: SetOverlayContextMessage) => {
    const p = msg.payload as Partial<SetOverlayContextMessage['payload']> | null | undefined;
    if (p === null || typeof p !== 'object') return;
    useCanvasOverlayStore.getState().setOverlayContext(readSource(p.source), readOverlayRows(p.rows));
  },
  setOverlayRegion: (msg: SetOverlayRegionMessage) => {
    useCanvasOverlayStore.getState().setOverlayRegion(readOverlayRegion(msg.payload));
  },
  setWhatsNewBadge: (msg: SetWhatsNewBadgeMessage) => {
    // `=== true`: a malformed push reads as "nothing new" — a dot is a claim, its absence is not.
    useAccountStore.getState().setWhatsNewBadge(msg.payload?.hasNew === true);
  },
  setOrientTargets: (msg: SetOrientTargetsMessage) => {
    const targets = readOrientTargets(msg.payload);
    if (targets === null) {
      diagLog('CanvasOverlay', `setOrientTargets dropped: malformed payload ${JSON.stringify(msg.payload)}`);
      return;
    }
    useCanvasOverlayStore.getState().setOrientTargets(targets);
  },
  setOverlayTopBar: (msg: SetOverlayTopBarMessage) => {
    useCanvasOverlayStore.getState().setOverlayTopBar(readOverlayTopBar(msg.payload));
  },
} satisfies HandlerMap;

// ─── Outbound ────────────────────────────────────────────────────────────

/** Nothing is left open on the page (sent after the last surface's exit animation). The outbox is
 *  FIFO, so every apply the closing surfaces flushed reaches Unity before this does. */
export function sendCloseOverlay(): void {
  enqueueSendToNative('closeOverlay', {});
}

/** The menu's Export / Export 3D rows. Unity closes the overlay itself and then runs the native
 *  export entry, so the page sends no `closeOverlay` after this. */
export function sendOverlayExport(kind: 'project' | 'objects'): void {
  enqueueSendToNative('overlayExport', { kind });
}

/** 20261002 - a coach-mark step became the current one on screen (the first one included). Unity
 *  opens a closed shot list for the add-shot step and restores it on the next one, so this rides
 *  the outbox, which pings at once. */
export function sendCanvasOrientStep(id: CanvasOrientTargetId): void {
  enqueueSendToNative('canvasOrientStep', { id });
}

/** 20261002 - the walk is over, exactly once per walk and BEFORE the page lets the overlay close
 *  (the outbox is FIFO, so it reaches Unity ahead of the closeOverlay that follows). Unity marks the
 *  walk seen on completed / skipped and leaves it unseen on aborted. */
export function sendCanvasOrientFinished(outcome: CanvasOrientOutcome): void {
  enqueueSendToNative('canvasOrientFinished', { outcome });
}

/** 20261002 - dev builds only: clear Unity's seen flag for the coach marks (the canvas hamburger's
 *  Reset Tutorial Flags (Dev) row). */
export function sendResetCanvasOrientSeen(): void {
  enqueueSendToNative('resetCanvasOrientSeen', {});
}
